import React, { useState, useRef, useEffect } from 'react';

const AudioPlayer = ({ 
  audioUrl, 
  title = "Audio", 
  showTitle = true,
  size = "normal", // "small", "normal", "large"
  theme = "agustinos", // "agustinos", "dark", "light"
  autoPlay = false // Nueva prop para reproducción automática
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Configuraciones de tema
  const themes = {
    agustinos: {
      primary: '#dc3545',
      background: '#f8f9fa',
      text: '#666',
      shadow: 'rgba(220, 53, 69, 0.3)'
    },
    dark: {
      primary: '#ffffff',
      background: '#2d3748',
      text: '#a0aec0',
      shadow: 'rgba(255, 255, 255, 0.2)'
    },
    light: {
      primary: '#4299e1',
      background: '#ffffff',
      text: '#718096',
      shadow: 'rgba(66, 153, 225, 0.3)'
    }
  };

  // Configuraciones de tamaño
  const sizes = {
    small: {
      padding: '12px',
      buttonSize: '36px',
      centralButton: '44px',
      fontSize: '11px',
      gap: '12px'
    },
    normal: {
      padding: '16px',
      buttonSize: '44px',
      centralButton: '56px',
      fontSize: '12px',
      gap: '20px'
    },
    large: {
      padding: '20px',
      buttonSize: '52px',
      centralButton: '64px',
      fontSize: '14px',
      gap: '24px'
    }
  };

  const currentTheme = themes[theme] || themes.agustinos;
  const currentSize = sizes[size] || sizes.normal;

  // Determinar el color del ícono basado en el tema
  const iconColor = theme === 'dark' ? '#000' : '#fff';

  // Componente para el ícono de play personalizado
  const PlayIcon = ({ size = 24, color = "#fff" }) => (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none"
      style={{ marginLeft: '2px' }} // Centrar ópticamente
    >
      <path
        d="M8 5v14l11-7z"
        fill={color}
        stroke={color}
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Componente para el ícono de pausa personalizado
  const PauseIcon = ({ size = 24, color = "#fff" }) => (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none"
    >
      <rect x="6" y="4" width="4" height="16" fill={color} rx="1"/>
      <rect x="14" y="4" width="4" height="16" fill={color} rx="1"/>
    </svg>
  );

  // Inicializar el audio cuando cambie la URL
  useEffect(() => {
    if (!audioUrl) {
      setError(true);
      setIsLoading(false);
      return;
    }

    if (audioRef.current) {
      const audio = audioRef.current;
      setError(false);
      setIsLoading(true);
      
      const handleLoadedMetadata = async () => {
        setDuration(audio.duration);
        setIsLoading(false);
        
        // Autoplay si está habilitado
        if (autoPlay && audio.duration > 0) {
          try {
            await audio.play();
            setIsPlaying(true);
            console.log('🎵 Reproducción automática iniciada');
          } catch (error) {
            console.warn('⚠️ Autoplay bloqueado por el navegador:', error);
            // El autoplay fue bloqueado, pero no es un error crítico
          }
        }
      };
      
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };
      
      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      const handleError = () => {
        setError(true);
        setIsLoading(false);
        console.error('Error cargando audio:', audioUrl);
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);

      // Forzar carga
      audio.load();

      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
      };
    }
  }, [audioUrl, autoPlay]);

  // Formatear tiempo en MM:SS
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle play/pause
  const togglePlayPause = async () => {
    if (audioRef.current) {
      try {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          await audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
      } catch (error) {
        console.error('Error reproduciendo audio:', error);
        setError(true);
      }
    }
  };

  // Saltar hacia atrás 10 segundos
  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
    }
  };

  // Saltar hacia adelante 10 segundos
  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
    }
  };

  // Manejar click en la barra de progreso
  const handleProgressClick = (e) => {
    if (audioRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * duration;
      audioRef.current.currentTime = newTime;
    }
  };

  // Calcular porcentaje de progreso
  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  // Si hay error, mostrar mensaje
  if (error) {
    return (
      <div style={{
        backgroundColor: currentTheme.background,
        borderRadius: '12px',
        padding: currentSize.padding,
        margin: '20px 0',
        textAlign: 'center',
        border: `1px solid ${currentTheme.primary}20`
      }}>
        <div style={{ color: currentTheme.text, fontSize: '14px' }}>
          ❌ Error cargando audio
        </div>
        {audioUrl && (
          <div style={{ fontSize: '11px', color: currentTheme.text, marginTop: '5px', opacity: 0.7 }}>
            URL: {audioUrl.substring(0, 50)}...
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: currentTheme.background,
      borderRadius: '12px',
      padding: currentSize.padding,
      margin: '20px auto',
      boxShadow: `0 2px 8px ${currentTheme.shadow}`,
      border: `1px solid ${currentTheme.primary}10`,
      maxWidth: '500px'
    }}>
      {/* Audio element oculto */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      {/* Título del audio */}
      {showTitle && (
        <div style={{
          textAlign: 'center',
          marginBottom: '12px',
          fontSize: '14px',
          color: currentTheme.text,
          fontWeight: '500'
        }}>
          🎵 {title}
        </div>
      )}

      {/* Barra de progreso */}
      <div style={{
        marginBottom: '16px',
        position: 'relative'
      }}>
        {/* Contenedor de la barra */}
        <div 
          onClick={handleProgressClick}
          style={{
            width: '100%',
            height: '6px',
            backgroundColor: `${currentTheme.primary}20`,
            borderRadius: '3px',
            cursor: isLoading ? 'default' : 'pointer',
            position: 'relative'
          }}
        >
          {/* Progreso */}
          <div style={{
            width: `${progressPercentage}%`,
            height: '100%',
            backgroundColor: currentTheme.primary,
            borderRadius: '3px',
            transition: 'width 0.1s ease'
          }} />
          
          {/* Círculo selector */}
          {!isLoading && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: `${progressPercentage}%`,
              transform: 'translate(-50%, -50%)',
              width: '16px',
              height: '16px',
              backgroundColor: currentTheme.primary,
              borderRadius: '50%',
              border: '2px solid white',
              boxShadow: `0 2px 4px ${currentTheme.shadow}`,
              cursor: 'pointer',
              opacity: duration > 0 ? 1 : 0
            }} />
          )}
        </div>

        {/* Tiempos */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '8px',
          fontSize: currentSize.fontSize,
          color: currentTheme.text
        }}>
          <span>{formatTime(currentTime)}</span>
          <span>{isLoading ? "Cargando..." : formatTime(duration)}</span>
        </div>
      </div>

      {/* Controles de reproducción */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: currentSize.gap
      }}>
        {/* Botón retroceder 10s */}
        <button
          onClick={skipBackward}
          disabled={isLoading || duration === 0}
          style={{
            width: currentSize.buttonSize,
            height: currentSize.buttonSize,
            borderRadius: '50%',
            border: `2px solid ${currentTheme.primary}`,
            backgroundColor: 'transparent',
            color: currentTheme.primary,
            cursor: isLoading ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: currentSize.fontSize,
            fontWeight: 'bold',
            transition: 'all 0.2s ease',
            opacity: isLoading ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = currentTheme.primary;
              e.target.style.color = theme === 'dark' ? '#000' : '#fff';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = currentTheme.primary;
            }
          }}
        >
          -10
        </button>

        {/* Botón play/pause central */}
        <button
          onClick={togglePlayPause}
          disabled={isLoading || duration === 0}
          style={{
            width: currentSize.centralButton,
            height: currentSize.centralButton,
            borderRadius: '50%',
            border: 'none',
            backgroundColor: currentTheme.primary,
            color: theme === 'dark' ? '#000' : '#fff',
            cursor: isLoading ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            transition: 'transform 0.1s ease',
            boxShadow: `0 4px 12px ${currentTheme.shadow}`,
            opacity: isLoading ? 0.7 : 1
          }}
          onMouseDown={(e) => !isLoading && (e.target.style.transform = 'scale(0.95)')}
          onMouseUp={(e) => !isLoading && (e.target.style.transform = 'scale(1)')}
          onMouseLeave={(e) => !isLoading && (e.target.style.transform = 'scale(1)')}
        >
          {isLoading ? (
            <div style={{
              width: '20px',
              height: '20px',
              border: `2px solid transparent`,
              borderTop: `2px solid ${iconColor}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          ) : (
            isPlaying ? (
              <PauseIcon size={20} color={iconColor} />
            ) : (
              <PlayIcon size={20} color={iconColor} />
            )
          )}
        </button>

        {/* Botón adelantar 10s */}
        <button
          onClick={skipForward}
          disabled={isLoading || duration === 0}
          style={{
            width: currentSize.buttonSize,
            height: currentSize.buttonSize,
            borderRadius: '50%',
            border: `2px solid ${currentTheme.primary}`,
            backgroundColor: 'transparent',
            color: currentTheme.primary,
            cursor: isLoading ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: currentSize.fontSize,
            fontWeight: 'bold',
            transition: 'all 0.2s ease',
            opacity: isLoading ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = currentTheme.primary;
              e.target.style.color = theme === 'dark' ? '#000' : '#fff';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = currentTheme.primary;
            }
          }}
        >
          +10
        </button>
      </div>

      {/* CSS para animación de loading */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AudioPlayer;